import React, { useEffect, useState } from 'react';
import { acceptMatch, declineMatch } from '../../api/quickDuel';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import '../../css/duels/MatchFoundModal.css';

/**
 * Модальное окно найденного матча для быстрых дуэлей
 * @param {Object} props
 * @param {Object|null} props.event - Событие MATCH_FOUND или TEAM_MATCH_FOUND
 * @param {Function} props.onAccept - Обработчик принятия матча
 * @param {Function} props.onDecline - Обработчик отклонения матча
 */
function MatchFoundModal({ event, onAccept, onDecline }) {
    const [countdown, setCountdown] = useState(30);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!event) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleDecline();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [event]);

    // Сброс таймера и состояния при новом событии
    useEffect(() => {
        if (event) {
            setCountdown(30);
            setIsAccepting(false);
            setIsDeclining(false);
            setError(null);
        }
    }, [event]);

    const handleAccept = async () => {
        if (!event?.duelId) return;

        setIsAccepting(true);
        setError(null);

        try {
            await acceptMatch(event.duelId);
            onAccept();
        } catch (err) {
            setError(err.response?.data?.message || 'Не удалось принять матч');
            setIsAccepting(false);
        }
    };

    const handleDecline = async () => {
        if (!event?.duelId) {
            onDecline();
            return;
        }

        setIsDeclining(true);
        setError(null);

        try {
            await declineMatch(event.duelId);
            onDecline();
        } catch (err) {
            setError(err.response?.data?.message || 'Не удалось отклонить матч');
            setIsDeclining(false);
        }
    };

    if (!event) return null;

    const isTeamMatch = event.type === 'TEAM_MATCH_FOUND';

    const formatLabels = {
        SOLO: '1v1',
        TEAM_2V2: '2v2',
        ANY: 'Любой'
    };

    const roundTypeLabels = {
        SINGLE_ROUND: 'Один раунд',
        BEST_OF_THREE: 'До 2 побед'
    };

    return (
        <HrnbModal
            isOpen={!!event}
            onClose={handleDecline}
            title="🎯 Матч найден!"
            size="md"
        >
            <div className="match-found-modal">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {!isTeamMatch ? (
                    <div className="match-found-modal__opponent">
                        <div className="match-found-modal__opponent-info">
                            <div className="match-found-modal__avatar-wrapper">
                                <img
                                    src={`/api/files/avatars/${event.opponentId}`}
                                    alt={event.opponentName}
                                    className="match-found-modal__avatar"
                                    onError={(e) => {
                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="8" r="4"/><path d="M12 14c-6 0-8 3-8 6v1h16v-1c0-3-2-6-8-6z"/></svg>';
                                    }}
                                />
                            </div>
                            <div className="match-found-modal__opponent-details">
                                <h3 className="match-found-modal__opponent-name">{event.opponentName}</h3>
                                <p className="match-found-modal__opponent-rating">
                                    🏆 Рейтинг: {event.opponentRating}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="match-found-modal__team">
                        <p className="match-found-modal__team-title">Командная дуэль 2v2</p>
                        <div className="match-found-modal__team-info">
                            <p>Товарищи по команде: {event.teammates?.length || 0}</p>
                            <p>Соперников: {event.opponents?.length || 0}</p>
                        </div>
                    </div>
                )}

                <div className="match-found-modal__details">
                    <div className="match-found-modal__detail">
                        <span className="label">Формат:</span>
                        <span className="value">{formatLabels[event.format] || event.format}</span>
                    </div>
                    <div className="match-found-modal__detail">
                        <span className="label">Тип:</span>
                        <span className="value">{roundTypeLabels[event.roundType] || event.roundType}</span>
                    </div>
                    {event.judgeIds && event.judgeIds.length > 0 && (
                        <div className="match-found-modal__detail">
                            <span className="label">Судей:</span>
                            <span className="value">{event.judgeIds.length}</span>
                        </div>
                    )}
                </div>

                <div className="match-found-modal__actions">
                    <HrnbButton
                        variant="primary"
                        onClick={handleAccept}
                        disabled={isAccepting || isDeclining}
                        size="lg"
                    >
                        {isAccepting ? 'Принятие...' : 'Принять'}
                    </HrnbButton>
                    <HrnbButton
                        variant="secondary"
                        onClick={handleDecline}
                        disabled={isAccepting || isDeclining}
                        size="lg"
                    >
                        {isDeclining ? 'Отклонение...' : 'Отклонить'}
                    </HrnbButton>
                </div>

                <div className="match-found-modal__countdown">
                    <HrnbLoader size="sm" />
                    <span>Автоматический отказ через: {countdown} сек</span>
                </div>
            </div>
        </HrnbModal>
    );
}

export default MatchFoundModal;
